import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal, Eyebrow } from "@/components/Motion";

const SECTIONS = [
  {
    title: "1. Who We Are",
    body: "NEXREQ UAE & GCC is a North American recruitment and talent solutions organization serving employers and professionals across the United States, Canada, the United Arab Emirates and the wider GCC. Our registered UAE office is Bin Dasmal Building – 1, Al Quoz Industrial First, Dubai, United Arab Emirates. You can reach us at info@nexreq.ae.",
  },
  {
    title: "2. Information We Collect",
    body: "When you use this website we may collect: contact details (name, email, phone number); professional information (job title, experience, sector, LinkedIn profile); curriculum vitae and supporting documents you upload; hiring requirement details submitted by employers; and technical data such as browser type, pages visited and approximate location derived from your IP address.",
  },
  {
    title: "3. How We Use Your Information",
    body: "We use personal data to: respond to enquiries; evaluate candidate profiles against current and future mandates; present candidates to prospective employers only with the candidate's knowledge; manage hiring requirements submitted by employers; operate, secure and improve this website; and meet legal or regulatory obligations. We do not sell personal data.",
  },
  {
    title: "4. Recruitment Data Processing & Consent",
    body: "Candidate profiles and resumes are processed for recruitment purposes on the basis of the consent you provide when submitting a form on this website. You may withdraw consent at any time by writing to info@nexreq.ae, and we will remove your profile from active consideration. Withdrawing consent does not affect the lawfulness of processing carried out before withdrawal.",
  },
  {
    title: "5. Confidentiality & Sharing",
    body: "Candidate information is treated as confidential. It is shared with a prospective employer only in the context of a specific opportunity and with the candidate's awareness. We may use trusted service providers (for example hosting and email delivery providers) who process data on our behalf under appropriate safeguards.",
  },
  {
    title: "6. Data Retention",
    body: "We retain candidate and client information for as long as it is relevant to active or anticipated recruitment activity, or as required by applicable law. You may request deletion of your data at any time (see Your Rights below).",
  },
  {
    title: "7. Data Security",
    body: "Resumes and uploaded documents are stored in access-controlled systems and are accessible only to authorized NEXREQ personnel. Administrative areas of this website are protected by authenticated access, session expiry and login attempt limiting.",
  },
  {
    title: "8. Your Rights",
    body: "Depending on your jurisdiction, you may have the right to access, correct, export or delete your personal data, and to object to or restrict certain processing. To exercise any of these rights, contact info@nexreq.ae and quote your submission reference ID where available.",
  },
  {
    title: "9. Cookies & Analytics",
    body: "This website uses privacy-conscious analytics to understand aggregate usage and improve the experience. No advertising trackers are used. You may block cookies in your browser settings; core website functionality will continue to work.",
  },
  {
    title: "10. International Transfers",
    body: "As a North American organization operating in the GCC, your data may be processed in more than one jurisdiction. We apply consistent confidentiality and security standards regardless of where processing occurs.",
  },
  {
    title: "11. Changes to This Policy",
    body: "We may update this policy from time to time. The current version is always published on this page. Last updated: September 2026.",
  },
  {
    title: "12. Contact",
    body: "Questions about this policy or your data: NEXREQ UAE & GCC, Bin Dasmal Building – 1, Al Quoz Industrial First, Dubai, United Arab Emirates — info@nexreq.ae.",
  },
];

const Privacy = () => (
  <>
    <Seo
      title="Privacy Policy — NEXREQ UAE & GCC"
      siteName="NEXREQ"
      description="How NEXREQ UAE & GCC collects, uses and protects candidate and client personal data."
    />
    <PageHero
      eyebrow="Legal"
      lines={["Privacy Policy."]}
      description="How NEXREQ collects, uses and protects personal data across its recruitment and talent advisory services."
    />
    <section className="bg-obsidian pb-28" data-testid="privacy-content">
      <div className="nx-container max-w-3xl">
        <Reveal>
          <div className="border border-white/10 bg-surface px-8 py-6">
            <Eyebrow>Candidate Privacy Notice</Eyebrow>
            <p className="mt-4 text-sm leading-relaxed text-white/55">
              Submitting a profile, resume or application on this website constitutes consent for
              NEXREQ to process your personal data for recruitment purposes. Your details are held
              in confidence, never sold, and shared with a prospective employer only with your
              knowledge. You may withdraw consent or request deletion at any time via{" "}
              <a href="mailto:info@nexreq.ae" className="text-emerald-400 hover:underline">
                info@nexreq.ae
              </a>
              .
            </p>
          </div>
        </Reveal>
        {SECTIONS.map((s) => (
          <Reveal key={s.title}>
            <div className="mt-12">
              <h2 className="font-heading text-xl font-light text-white">{s.title}</h2>
              <p className="mt-4 text-sm leading-relaxed text-white/55">{s.body}</p>
            </div>
          </Reveal>
        ))}
        <p className="mt-14 border-t border-white/10 pt-8 text-sm text-white/40">
          Also see our{" "}
          <Link to="/terms" className="text-emerald-400 hover:underline" data-testid="privacy-terms-link">
            Terms of Use
          </Link>
          .
        </p>
      </div>
    </section>
  </>
);

export default Privacy;
